/**
 * OrderLineItemsTable Component
 * Displays order line items with product details, quantities, pricing, and tax breakdown
 * Supports sorting by columns
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-004
 */

import { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import type { OrderLineItem, TaxBreakdown } from '@/types/order';

interface OrderLineItemsTableProps {
  lineItems: OrderLineItem[];
}

type SortField = 'productName' | 'quantity' | 'unitPrice' | 'discountAmount' | 'lineTotal';
type SortDirection = 'asc' | 'desc';

/**
 * Format currency to INR
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
  }).format(amount);
}

/**
 * Render tax breakdown badges
 */
function renderTaxBreakdown(taxBreakdown: TaxBreakdown[]) {
  return (
    <div className="flex flex-wrap gap-1">
      {taxBreakdown.map((tax, index) => (
        <Badge key={index} variant="outline" className="text-xs">
          {tax.taxType} {tax.rate}%: {formatCurrency(tax.amount)}
        </Badge>
      ))}
    </div>
  );
}

export function OrderLineItemsTable({ lineItems }: OrderLineItemsTableProps) {
  const [sortField, setSortField] = useState<SortField>('productName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  /**
   * Handle column sort
   */
  function handleSort(field: SortField) {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with ascending direction
      setSortField(field);
      setSortDirection('asc');
    }
  }

  /**
   * Get sort icon for column
   */
  function getSortIcon(field: SortField) {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="h-4 w-4" />
    ) : (
      <ArrowDown className="h-4 w-4" />
    );
  }

  /**
   * Sort line items
   */
  const sortedLineItems = useMemo(() => {
    const items = [...lineItems];

    items.sort((a, b) => {
      let aValue: string | number = '';
      let bValue: string | number = '';

      switch (sortField) {
        case 'productName':
          aValue = a.productName.toLowerCase();
          bValue = b.productName.toLowerCase();
          break;
        case 'quantity':
          aValue = a.quantity;
          bValue = b.quantity;
          break;
        case 'unitPrice':
          aValue = a.unitPrice;
          bValue = b.unitPrice;
          break;
        case 'discountAmount':
          aValue = a.discountAmount;
          bValue = b.discountAmount;
          break;
        case 'lineTotal':
          aValue = a.lineTotal;
          bValue = b.lineTotal;
          break;
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return items;
  }, [lineItems, sortField, sortDirection]);

  /**
   * Calculate totals
   */
  const totals = useMemo(() => {
    return lineItems.reduce(
      (acc, item) => ({
        quantity: acc.quantity + item.quantity,
        discount: acc.discount + item.discountAmount,
        tax: acc.tax + item.totalTax,
        total: acc.total + item.lineTotal,
      }),
      { quantity: 0, discount: 0, tax: 0, total: 0 }
    );
  }, [lineItems]);

  return (
    <Card className="border-slate-200 bg-white shadow-md">
      <CardHeader className="pb-4 border-b border-slate-200">
        <CardTitle className="text-xl text-slate-900">Order Line Items</CardTitle>
        <p className="text-sm text-slate-500 mt-1">
          {lineItems.length} item{lineItems.length !== 1 ? 's' : ''}
        </p>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold text-slate-700">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('productName')}
                    className="h-8 gap-1"
                  >
                    Product
                    {getSortIcon('productName')}
                  </Button>
                </TableHead>
                <TableHead className="font-semibold text-slate-700">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('quantity')}
                    className="h-8 gap-1"
                  >
                    Quantity
                    {getSortIcon('quantity')}
                  </Button>
                </TableHead>
                <TableHead className="font-semibold text-slate-700 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('unitPrice')}
                    className="h-8 gap-1"
                  >
                    Unit Price
                    {getSortIcon('unitPrice')}
                  </Button>
                </TableHead>
                <TableHead className="font-semibold text-slate-700 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('discountAmount')}
                    className="h-8 gap-1"
                  >
                    Discount
                    {getSortIcon('discountAmount')}
                  </Button>
                </TableHead>
                <TableHead className="font-semibold text-slate-700">Tax</TableHead>
                <TableHead className="font-semibold text-slate-700 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('lineTotal')}
                    className="h-8 gap-1"
                  >
                    Line Total
                    {getSortIcon('lineTotal')}
                  </Button>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedLineItems.map((item) => (
                <TableRow key={item.id} className="hover:bg-slate-50">
                  <TableCell>
                    <div className="space-y-1">
                      <p className="font-medium text-slate-900">{item.productName}</p>
                      <p className="text-xs text-slate-500">{item.orderLineNumber}</p>
                      {item.description && (
                        <p className="text-xs text-slate-500 max-w-xs truncate">
                          {item.description}
                        </p>
                      )}
                      <Badge variant="outline" className="text-xs">
                        {item.productType}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <p className="font-semibold text-slate-900">
                        {item.quantity.toLocaleString('en-IN')}
                      </p>
                      <p className="text-xs text-slate-500">{item.quantityUnit}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <p className="font-medium text-slate-900">
                      {formatCurrency(item.unitPrice)}
                    </p>
                    <p className="text-xs text-slate-500">per {item.quantityUnit}</p>
                  </TableCell>
                  <TableCell className="text-right">
                    {item.discountAmount > 0 ? (
                      <div className="space-y-1">
                        <p className="font-medium text-green-700">
                          -{formatCurrency(item.discountAmount)}
                        </p>
                        <p className="text-xs text-slate-500">
                          ({item.discountPercentage}%)
                        </p>
                      </div>
                    ) : (
                      <p className="text-slate-400">-</p>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <p className="font-medium text-slate-900">
                        {formatCurrency(item.totalTax)}
                      </p>
                      {renderTaxBreakdown(item.taxBreakdown)}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <p className="text-lg font-bold text-slate-900">
                      {formatCurrency(item.lineTotal)}
                    </p>
                  </TableCell>
                </TableRow>
              ))}

              {/* Totals Row */}
              <TableRow className="bg-slate-100 font-semibold border-t-2 border-slate-300">
                <TableCell className="text-slate-900">Total</TableCell>
                <TableCell>
                  <p className="font-bold text-slate-900">
                    {totals.quantity.toLocaleString('en-IN')}
                  </p>
                </TableCell>
                <TableCell></TableCell>
                <TableCell className="text-right">
                  <p className="font-bold text-green-700">
                    -{formatCurrency(totals.discount)}
                  </p>
                </TableCell>
                <TableCell>
                  <p className="font-bold text-slate-900">
                    {formatCurrency(totals.tax)}
                  </p>
                </TableCell>
                <TableCell className="text-right">
                  <p className="text-xl font-bold text-slate-900">
                    {formatCurrency(totals.total)}
                  </p>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
